import type { CSSProperties } from 'react';

import type { Template } from '@/app/dashboard/templates/components/types/template';
import {
  resolveReadableTextColor,
  resolveTemplateSurfacePalette,
  type TemplateSurfacePalette,
} from '@/app/dashboard/templates/components/lib/templateExperience';
import { resolveTemplateVariables } from '@/app/dashboard/templates/components/lib/templateVariables';
import { applyDesignOverrides, applyMenuThemeToTemplate } from './apply';
import type { DesignOverrides } from './design-overrides';
import { toHslTriplet } from './hsl';
import { getMenuTheme, resolveMenuThemeColors, type MenuThemePackage } from './registry';

export type BranchPageTheme = {
  light: TemplateSurfacePalette;
  dark: TemplateSurfacePalette;
  /** Optional authored backdrop gradient from a selected menu theme package. */
  backgroundPaint?: { light: string; dark: string };
};
type PaletteStyle = CSSProperties & Record<`--branch-${string}`, string>;

/**
 * Resolve the branch's public palette using the same precedence as its menu:
 * explicit branch/menu theme first, then the selected template plus its design
 * overrides. A platform-default template by itself is not merchant branding.
 */
export function buildBranchPageTheme(input: {
  schema?: Template | null;
  designOverrides?: DesignOverrides | null;
  themeId?: string | null;
  themePackage?: MenuThemePackage | null;
}): BranchPageTheme | null {
  const theme = input.themePackage || getMenuTheme(input.themeId);
  if (theme && input.designOverrides && Object.keys(input.designOverrides).length) {
    const base = input.schema || ({ version: '1', variables: {} } as Template);
    const schema = applyDesignOverrides(applyMenuThemeToTemplate(base, theme), input.designOverrides);
    const paint = (mode: 'light' | 'dark') => {
      const colors = resolveTemplateVariables(schema, mode).colors;
      return colors.backgroundGradient && colors.backgroundGradient !== 'none' ? String(colors.backgroundGradient) : String(colors.background);
    };
    return { light: resolveTemplateSurfacePalette(schema, 'light'), dark: resolveTemplateSurfacePalette(schema, 'dark'), backgroundPaint: { light: paint('light'), dark: paint('dark') } };
  }
  if (theme) {
    const light = resolveMenuThemeColors(theme, 'light').colors;
    const dark = resolveMenuThemeColors(theme, 'dark').colors;
    return {
      light: menuThemePalette(theme, 'light'),
      dark: menuThemePalette(theme, 'dark'),
      backgroundPaint: {
        light: light.gradients?.background || light.background,
        dark: dark.gradients?.background || dark.background,
      },
    };
  }

  const source = input.schema;
  const slug = typeof source?.meta?.slug === 'string' ? source.meta.slug : '';
  if (!source || slug === 'platform-default') return null;
  const schema = input.designOverrides
    ? applyDesignOverrides(source, input.designOverrides)
    : source;
  return {
    light: resolveTemplateSurfacePalette(schema, 'light'),
    dark: resolveTemplateSurfacePalette(schema, 'dark'),
  };
}

function menuThemePalette(theme: NonNullable<ReturnType<typeof getMenuTheme>>, mode: 'light' | 'dark'): TemplateSurfacePalette {
  const { colors } = resolveMenuThemeColors(theme, mode);
  // Keep semantic background tokens as a solid color (they are also converted
  // to HSL triplets for shadcn). Authored gradients are preserved separately as
  // the page backdrop paint by buildBranchPageTheme.
  const background = colors.background;
  const foreground = resolveReadableTextColor(background, colors.text);
  const cardForeground = resolveReadableTextColor(colors.surface, foreground);
  const mutedForeground = resolveReadableTextColor(colors.secondary, colors.muted);
  const accentForeground = foreground;
  const primaryForeground = foreground;
  const promoBg = colors.gradients?.promo || colors.promoBg;

  return {
    background,
    foreground,
    muted: colors.secondary,
    mutedForeground,
    card: colors.surface,
    cardForeground,
    border: colors.divider,
    accent: colors.accent,
    accentForeground,
    primary: colors.primary,
    primaryForeground,
    promoBg,
    promoText: resolveReadableTextColor(promoBg, colors.promoText),
    promoBorder: colors.promoBorder,
    panelGlass: `color-mix(in srgb, ${background} 88%, ${foreground} 12%)`,
    panelShadow: '0 12px 32px rgb(0 0 0 / 0.12)',
    overlay: `color-mix(in srgb, ${foreground} 55%, transparent)`,
  };
}

/** CSS variables for both modes; the root data-theme attribute selects one. */
export function branchPageThemeStyle(theme: BranchPageTheme): PaletteStyle {
  const style: Record<string, string> = {};
  if (theme.backgroundPaint?.light) style['--branch-light-background-paint'] = theme.backgroundPaint.light;
  if (theme.backgroundPaint?.dark) style['--branch-dark-background-paint'] = theme.backgroundPaint.dark;
  for (const mode of ['light', 'dark'] as const) {
    const palette = theme[mode];
    const prefix = `--branch-${mode}-`;
    const values: Record<string, string> = {
      background: palette.background,
      foreground: palette.foreground,
      card: palette.card,
      cardForeground: palette.cardForeground,
      muted: palette.muted,
      mutedForeground: palette.mutedForeground,
      border: palette.border,
      accent: palette.accent,
      accentForeground: palette.accentForeground,
      primary: palette.primary,
      primaryForeground: palette.primaryForeground,
      promoBg: palette.promoBg,
      promoText: palette.promoText,
      promoBorder: palette.promoBorder,
    };
    const fallbackByKey: Record<string, string> = {
      background: mode === 'dark' ? '#0f0f0f' : '#f6f1e3',
      foreground: mode === 'dark' ? '#f5f5f5' : '#1a1814',
      card: palette.background,
      cardForeground: palette.foreground,
      muted: palette.background,
      mutedForeground: palette.foreground,
      border: palette.background,
      accent: palette.primary,
      accentForeground: palette.foreground,
      primary: palette.foreground,
      primaryForeground: palette.accentForeground,
    };
    for (const [key, value] of Object.entries(values)) {
      style[`${prefix}${key}`] = value;
      if (['background', 'foreground', 'card', 'cardForeground', 'muted', 'mutedForeground', 'border', 'accent', 'accentForeground', 'primary', 'primaryForeground'].includes(key)) {
        style[`${prefix}${key}Hsl`] = safeTokenHsl(
          value,
          fallbackByKey[key] ?? palette.foreground,
          mode === 'dark' ? '#0f0f0f' : '#f6f1e3',
        );
      }
    }
  }
  return style as PaletteStyle;
}

function safeTokenHsl(value: string, fallback: string, finalFallback: string): string {
  const tripletPattern = /^-?[\d.]+\s+[\d.]+%\s+[\d.]+%$/;
  const triplet = toHslTriplet(value);
  if (tripletPattern.test(triplet)) return triplet;
  const fallbackTriplet = toHslTriplet(fallback);
  return tripletPattern.test(fallbackTriplet) ? fallbackTriplet : toHslTriplet(finalFallback);
}
